/**
 * WHO Assessment Results View
 * Displays post-submission results for scored and checklist WHO assessments
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './WHOAssessmentResults.css';

const WHOAssessmentResults = ({ result, instrumentType, onRedo, onViewProfile, onBackToList }) => {
  const { t, i18n } = useTranslation(['qms', 'common']);
  const useRo = (i18n.language || 'ro').startsWith('ro');

  if (!result) {
    return (
      <div className="who-results">
        <p>{t('common:error')}</p>
      </div>
    );
  }

  if (instrumentType === 'checklist') {
    return renderChecklistResults();
  }

  if (instrumentType === 'checklist753') {
    return renderChecklist753Results();
  }

  return renderScoredResults();

  function renderChecklist753Results() {
    const { instrument, totalPercentage = 0, totalApplicable = 0, totalScore = 0, totalQuestions = 105, excludedNa = 0, themeScores = [] } = result;
    return (
      <div className="who-results">
        <div className="who-results-header">
          <h2>{t('qms:whoAssessments.resultsTitle') || 'Assessment Results'}</h2>
          <div className="instrument-name">
            {useRo ? (instrument?.nameRo || instrument?.name) : (instrument?.name || instrument?.nameRo)}
          </div>
        </div>

        <div className="results-summary">
          <div className="summary-card overall">
            <div className="summary-label">{t('qms:whoAssessments.totalScore') || 'Total Score'}</div>
            <div className="summary-value">{totalPercentage}%</div>
            <div className="summary-detail">
              {totalScore} / {totalApplicable} {t('qms:whoAssessments.applicableQuestions') || 'applicable questions'}
              {totalQuestions > totalApplicable && ` (${totalQuestions} ${t('qms:whoAssessments.total') || 'total'})`}
            </div>
          </div>
          {excludedNa > 0 && (
            <div className="summary-note">
              {excludedNa} {t('qms:whoAssessments.questionsExcludedNa') || 'questions excluded (N/A)'}
            </div>
          )}
        </div>

        <div className="results-section">
          <h3>{t('qms:whoAssessments.themeBreakdown') || 'Theme Breakdown'}</h3>
          <div className="phase-stats">
            {themeScores.map((theme, idx) => (
              <div key={theme.themeId || idx} className="phase-stat-card">
                <div className="phase-stat-name">{theme.nameRo || theme.name}</div>
                <div className="phase-stat-bar">
                  <div className="phase-stat-fill" style={{ width: `${theme.percentage || 0}%` }} />
                </div>
                <div className="phase-stat-text">
                  {theme.percentage}% ({theme.score}/{theme.applicable})
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="results-actions">
          <button className="btn btn-secondary" onClick={onRedo}>
            {t('qms:whoAssessments.redoAssessment') || 'Redo Assessment'}
          </button>
          {onBackToList && (
            <button className="btn btn-secondary" onClick={onBackToList}>
              {t('qms:whoAssessments.backToList') || 'Back to list'}
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderChecklistResults() {
    const { phases = [], answers = {}, instrument } = result;
    const allItems = [];
    const phaseStats = [];

    phases.forEach((phase) => {
      const items = phase.items || [];
      const answeredYes = items.filter((item) => answers[item.id] === 'yes').length;
      const total = items.length;
      const completionPct = total > 0 ? Math.round((answeredYes / total) * 100) : 0;

      phaseStats.push({
        phaseName: useRo ? (phase.nameRo || phase.name) : (phase.name || phase.nameRo),
        answeredYes,
        total,
        completionPct
      });

      items.forEach((item) => {
        allItems.push({
          ...item,
          phaseName: useRo ? (phase.nameRo || phase.name) : (phase.name || phase.nameRo),
          answer: answers[item.id]
        });
      });
    });

    const totalAnsweredYes = allItems.filter((item) => item.answer === 'yes').length;
    const totalItems = allItems.length;
    const overallCompletion = totalItems > 0 ? Math.round((totalAnsweredYes / totalItems) * 100) : 0;

    const gaps = allItems.filter((item) => item.answer !== 'yes');
    const gapsByPhase = {};
    gaps.forEach((gap) => {
      if (!gapsByPhase[gap.phaseName]) {
        gapsByPhase[gap.phaseName] = [];
      }
      gapsByPhase[gap.phaseName].push(gap);
    });

    return (
      <div className="who-results">
        <div className="who-results-header">
          <h2>{t('qms:whoAssessments.resultsTitle') || 'Assessment Results'}</h2>
          <div className="instrument-name">
            {useRo ? (instrument?.nameRo || instrument?.name) : (instrument?.name || instrument?.nameRo)}
          </div>
        </div>

        <div className="results-summary">
          <div className="summary-card overall">
            <div className="summary-label">{t('qms:whoAssessments.overallCompletion') || 'Overall Completion'}</div>
            <div className="summary-value">{overallCompletion}%</div>
            <div className="summary-detail">
              {totalAnsweredYes} / {totalItems} {t('qms:whoAssessments.itemsCompleted') || 'items completed'}
            </div>
          </div>
        </div>

        <div className="results-section">
          <h3>{t('qms:whoAssessments.phaseBreakdown') || 'Phase Breakdown'}</h3>
          <div className="phase-stats">
            {phaseStats.map((stat, idx) => (
              <div key={idx} className="phase-stat-card">
                <div className="phase-stat-name">{stat.phaseName}</div>
                <div className="phase-stat-bar">
                  <div className="phase-stat-fill" style={{ width: `${stat.completionPct}%` }} />
                </div>
                <div className="phase-stat-text">
                  {stat.completionPct}% ({stat.answeredYes}/{stat.total})
                </div>
              </div>
            ))}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="results-section gaps-section">
            <h3>{t('qms:whoAssessments.identifiedGaps') || 'Identified Gaps'}</h3>
            <p className="gaps-intro">
              {t('qms:whoAssessments.gapsIntro') || 'Items that were not marked as "Yes":'}
            </p>
            {Object.keys(gapsByPhase).map((phaseName) => (
              <div key={phaseName} className="gaps-phase">
                <h4 className="gaps-phase-title">{phaseName}</h4>
                <ul className="gaps-list">
                  {gapsByPhase[phaseName].map((gap) => (
                    <li key={gap.id} className="gap-item">
                      <span className="gap-code">{gap.code}</span>
                      <span className="gap-text">
                        {useRo ? (gap.textRo || gap.textEn) : (gap.textEn || gap.textRo)}
                      </span>
                      <span className={`gap-answer gap-answer-${gap.answer || 'unanswered'}`}>
                        {gap.answer || t('qms:whoAssessments.unanswered') || 'Unanswered'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className="results-actions">
          <button className="btn btn-secondary" onClick={onRedo}>
            {t('qms:whoAssessments.redoAssessment') || 'Redo Assessment'}
          </button>
          {onBackToList && (
            <button className="btn btn-secondary" onClick={onBackToList}>
              {t('qms:whoAssessments.backToList') || 'Back to list'}
            </button>
          )}
          {onViewProfile && (
            <button className="btn btn-primary" onClick={onViewProfile}>
              {t('qms:whoAssessments.viewCapabilityProfile') || 'View Capability Profile'}
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderScoredResults() {
    const { score = 0, maxScore = 0, maturityLevel, components = [], instrument } = result;
    const maturityLevelLabel = maturityLevel?.label || maturityLevel?.labelRo || '';

    const componentStats = components.map((comp) => {
      const compMaxScore = comp.maxScore || 0;
      const compScore = comp.score || 0;
      const pct = compMaxScore > 0 ? Math.round((compScore / compMaxScore) * 100) : 0;
      return {
        name: useRo ? (comp.nameRo || comp.name) : (comp.name || comp.nameRo),
        score: compScore,
        maxScore: compMaxScore,
        pct
      };
    });

    const needsAttention = componentStats.filter((stat) => stat.pct < 50);

    return (
      <div className="who-results">
        <div className="who-results-header">
          <h2>{t('qms:whoAssessments.resultsTitle') || 'Assessment Results'}</h2>
          <div className="instrument-name">
            {useRo ? (instrument?.nameRo || instrument?.name) : (instrument?.name || instrument?.nameRo)}
          </div>
        </div>

        <div className="results-summary">
          <div className="summary-card score">
            <div className="summary-label">{t('qms:whoAssessments.totalScore') || 'Total Score'}</div>
            <div className="summary-value">
              {score} / {maxScore}
            </div>
          </div>
          {maturityLevelLabel && (
            <div className="summary-card maturity">
              <div className="summary-label">{t('qms:whoAssessments.maturityLevel') || 'Maturity Level'}</div>
              <div className="summary-value maturity-label">{maturityLevelLabel}</div>
            </div>
          )}
        </div>

        <div className="results-section">
          <h3>{t('qms:whoAssessments.componentBreakdown') || 'Component Breakdown'}</h3>
          <div className="component-stats">
            {componentStats.map((stat, idx) => (
              <div key={idx} className="component-stat-card">
                <div className="component-stat-name">{stat.name}</div>
                <div className="component-stat-bar">
                  <div
                    className={`component-stat-fill ${stat.pct < 50 ? 'low' : ''}`}
                    style={{ width: `${stat.pct}%` }}
                  />
                </div>
                <div className="component-stat-text">
                  {stat.score} / {stat.maxScore} ({stat.pct}%)
                </div>
              </div>
            ))}
          </div>
        </div>

        {needsAttention.length > 0 && (
          <div className="results-section attention-section">
            <h3>{t('qms:whoAssessments.needsAttention') || 'Needs Attention'}</h3>
            <p className="attention-intro">
              {t('qms:whoAssessments.attentionIntro') || 'Components scoring below 50%:'}
            </p>
            <ul className="attention-list">
              {needsAttention.map((stat, idx) => (
                <li key={idx} className="attention-item">
                  <span className="attention-name">{stat.name}</span>
                  <span className="attention-score">
                    {stat.score}/{stat.maxScore} ({stat.pct}%)
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="results-actions">
          <button className="btn btn-secondary" onClick={onRedo}>
            {t('qms:whoAssessments.redoAssessment') || 'Redo Assessment'}
          </button>
          {onBackToList && (
            <button className="btn btn-secondary" onClick={onBackToList}>
              {t('qms:whoAssessments.backToList') || 'Back to list'}
            </button>
          )}
        </div>
      </div>
    );
  }
};

export default WHOAssessmentResults;
